import { useState, useEffect } from 'react';
import { optimizeGoogleProfilePicture, createGoogleProfileFallback, getUserColor } from '../../utils/imageUtils';
import { avatarCache } from '../../utils/avatarCache';
import { env } from '../../config/env';

interface ProfileImageProps {
  src?: string;
  alt: string;
  name?: string;
  email?: string;
  userId?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ProfileImage({ 
  src, 
  alt, 
  name, 
  email, 
  userId,
  size = 'md', 
  className = '' 
}: ProfileImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const [retryCount, setRetryCount] = useState(0);

  // Helper to resolve avatar URL
  const resolveAvatarUrl = (url: string): string => {
    if (!url) return url;
    // If it's a relative API path, prepend the API base URL
    if (url.startsWith('/api/')) {
      return `${env.API_BASE_URL}${url}`;
    }
    return url;
  };

  // Reset error state and load cached avatar when src changes
  useEffect(() => {
    if (src) {
      setImageError(false);
      setIsLoading(true);
      setRetryCount(0);
      
      // Use avatar cache if userId is provided
      if (userId) {
        avatarCache.getAvatar(userId, src)
          .then((cachedSrc) => {
            setCurrentSrc(cachedSrc);
          })
          .catch(() => {
            // Fallback to optimized Google URL or resolved URL
            const resolvedSrc = resolveAvatarUrl(src);
            const optimizedSrc = optimizeGoogleProfilePicture(resolvedSrc);
            setCurrentSrc(optimizedSrc);
          });
      } else {
        // Fallback to optimized Google URL or resolved URL
        const resolvedSrc = resolveAvatarUrl(src);
        const optimizedSrc = optimizeGoogleProfilePicture(resolvedSrc);
        setCurrentSrc(optimizedSrc);
      }
    }
  }, [src, userId]);

  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg'
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    
    // Try fallback URL for Google images on first error
    if (retryCount === 0 && src && src.includes('googleusercontent.com')) {
      setRetryCount(1);
      const fallbackSrc = createGoogleProfileFallback(src);
      setCurrentSrc(fallbackSrc);
      return;
    }
    
    // Give up and show initials
    setImageError(true);
  };

  // Get initials for fallback
  const getInitials = () => {
    if (name) {
      return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return '?';
  };

  // Get user-specific color
  const userColor = getUserColor(email || name || '');

  // If no src provided or image failed to load, show initials
  if (!currentSrc || imageError) {
    return (
      <div className={`${sizeClasses[size]} ${userColor} rounded-full flex items-center justify-center ${className}`}>
        <span className="text-white font-medium">
          {getInitials()}
        </span>
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-full flex items-center justify-center">
          <span className="text-gray-400 text-xs">...</span>
        </div>
      )}
      <img
        src={currentSrc}
        alt={alt}
        className={`h-full w-full object-cover rounded-full ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
      />
    </div>
  );
}